// Clientes del portal (`portal-vendedor-clientes.html`): pestañas Clientes y Prospectos sobre el
// mismo marco. La pestaña viaja en la URL (`?tab=prospects`) para que los enlaces lleguen a ella.
import { Clock, Users } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import type { Permission } from '@/api/types/permissions';
import { useProspects } from '@/domains/sales/wizard/api/useWizard';
import { useSellerContext } from '@/domains/sales/wizard/sellerContext';
import { useAuth } from '@/hooks/useAuth';
import { PageTabs, pageTabIds, type PageTab } from '@/shared/components/PageTabs';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import { usePortalCustomers } from './customers/api/usePortalCustomers';
import { CustomersExplorer } from './customers/CustomersExplorer';
import { customersTabCount, prospectsTabCount } from './customers/lib/customersTabCounts';
import type { CustomersSectionTab } from './salesSectionSearch';
import { SalesProspects } from './SalesProspects';

/** El permiso que exige `GET /prospects` en el silo del wizard. */
export const PROSPECTS_PERMISSION = 'retail.wizard.operate' as Permission;

/** El marco de los dos explorers: el mismo para que cambiar de pestaña no mueva el layout. */
const EXPLORER_FRAME = 'm-2 mt-0 rounded-[10px] border border-border bg-card';

/** Prefijo de los ids que enlazan cada pestaña con su panel. */
const TABS_ID = 'sales-customers';

/** Misma llave que la cartera sin búsqueda: el contador no dispara otra lectura. */
const NO_SERVER_QUERY = {};

export function SalesCustomers({
    tab = 'customers',
    customerId,
    prospectId,
}: {
    tab?: CustomersSectionTab;
    customerId?: string;
    prospectId?: string;
}) {
    const navigate = useNavigate();
    const { sellerId } = useSellerContext();
    const { hasPermission } = useAuth();
    const canSeeProspects = hasPermission(PROSPECTS_PERMISSION);
    const customers = usePortalCustomers(NO_SERVER_QUERY);
    const prospects = useProspects(sellerId, canSeeProspects);
    // Sin permiso, el enlace a Prospectos cae en Clientes.
    const activeTab: CustomersSectionTab = canSeeProspects ? tab : 'customers';

    const customersCount = customersTabCount({
        isPending: customers.isPending,
        failed: customers.error !== null && customers.error !== undefined,
        scope: { loaded: customers.rows.length, hasMore: customers.hasNextPage },
    });
    const tabs: PageTab<CustomersSectionTab>[] = [
        {
            key: 'customers',
            label: 'Clientes',
            icon: Users,
            count: customersCount.count,
            countTitle: customersCount.title,
        },
    ];
    if (canSeeProspects) {
        const prospectsCount = prospectsTabCount({
            enabled: sellerId !== '',
            failed: prospects.isError,
            list: prospects.data,
        });
        tabs.push({
            key: 'prospects',
            label: 'Prospectos',
            icon: Clock,
            count: prospectsCount.count,
            countTitle: prospectsCount.title,
        });
    }

    const changeTab = (next: CustomersSectionTab) => {
        void navigate({
            to: '/sales/$section',
            params: { section: 'customers' },
            search: next === 'prospects' ? { tab: 'prospects' } : {},
            replace: true,
        });
    };

    return (
        <ScreenFrame>
            <PageTabs
                variant="inline"
                ariaLabel="Clientes y prospectos"
                idPrefix={TABS_ID}
                className="mx-2 mb-2 mt-2"
                tabs={tabs}
                active={activeTab}
                onChange={changeTab}
            />
            <div
                role="tabpanel"
                id={pageTabIds(TABS_ID, activeTab).panel}
                aria-labelledby={pageTabIds(TABS_ID, activeTab).tab}
                className="flex min-h-0 flex-1 flex-col"
            >
                {activeTab === 'prospects' ? (
                    <SalesProspects prospectId={prospectId} className={EXPLORER_FRAME} />
                ) : (
                    <CustomersExplorer customerId={customerId} className={EXPLORER_FRAME} />
                )}
            </div>
        </ScreenFrame>
    );
}
